import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../atoms/button";
import LoadingOverlay from "../atoms/loadingOverlay";
import ToastContext from "../../context/toastProvider";
import { statusLabel } from "../artLabels/status";
import { conditionLabel } from "./conditions";
import { deleteExtendedArtSerial, extendedArtOutMessage } from "./functions";
import { formatDateTime } from "./format";
import FinalizeItemModal from "./FinalizeItemModal";
import ArtLabelStep from "./ArtLabelStep";

const Info = ({ label, children }) => (
    <div className="w-32">
        <p className="m-0 -mb-1 text-sm text-[#999] text-center">{label}</p>
        <p className="px-2 text-center break-words">{children}</p>
    </div>
);

/** Linha do item de arte estendida na área de itens do pedido (spec 2026-10-01 P7), no visual do `Serial`. */
const ExtendedArtItemRow = ({ user, item, order, index, refresh }) => {
    const navigate = useNavigate();
    const { goodToast, badToast } = useContext(ToastContext);
    const [confirm, setConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [changingArt, setChangingArt] = useState(false);

    const model = item?.gradableModel || {};
    const art = item?.artLabel || null;

    const doDelete = async () => {
        setLoading(true);
        const out = await deleteExtendedArtSerial(user?.token, item._id);
        setLoading(false);
        setConfirm(false);
        if (out?.type !== "success") {
            badToast(extendedArtOutMessage(out, "Erro ao excluir o item"));
            return;
        }
        goodToast("Item excluído");
        refresh();
    };

    return (
        <div className="border border-primary p-2 bg-black mb-4 relative">
            {item?.isDraft ? <div className="absolute top-2 left-10 bg-green-300 px-2 py-1 text-black">NOVO</div> : null}
            <div className="absolute top-1 left-2">
                <p className="text-[#666] text-sm">{index + 1}</p>
            </div>
            {
                item?.isDraft ?
                    <h3 className="text-center mb-4 text-green-500 font-bold">Rascunho de item</h3>
                    :
                    <h3 className="text-center mb-4">Serial: {item?.serialNumber}</h3>
            }
            <div className="flex justify-center gap-4 flex-wrap items-center">
                <div className="flex items-center gap-2">
                    {art?.thumbUrl ? <img alt={`Arte ${art.cardName || model.name || ""}`.trim()} src={art.thumbUrl} className="h-16 rounded" /> : null}
                    <Info label="Arte">{art ? statusLabel(art.status) : "sem arte"}</Info>
                </div>
                <Info label="Nome">{model.name}</Info>
                <Info label="Edição">{model.edition}</Info>
                <Info label="Numeração">{model.numbering}</Info>
                <Info label="Etiqueta gerada em">{item?.tagGeneratedAt ? formatDateTime(item.tagGeneratedAt) : "não gerada"}</Info>
                <Info label="Condição">{item?.condition ? `${item.condition} — ${conditionLabel(item.condition)}` : "—"}</Info>
                {order?.autographEvaluation ? <Info label="Autógrafo">{item?.notaAutografo ?? "—"}</Info> : null}
            </div>
            <div className="flex justify-center gap-2 flex-wrap mt-4">
                <Button classes={item?.isDraft ? "bg-green-400 hover:bg-green-300" : "bg-yellow-300 hover:bg-yellow-200"} click={() => setFinalizing(true)}>
                    {item?.isDraft ? "Finalizar" : "Corrigir"}
                </Button>
                <Button classes="bg-primary" click={() => setChangingArt(true)}>Trocar arte</Button>
                {art?.artKey ? (
                    <Button classes="bg-purple-300 hover:bg-purple-400" click={() => navigate(`/manafix/art-labels/${encodeURIComponent(art.artKey)}`)}>Abrir revisão</Button>
                ) : null}
                {
                    confirm ?
                        <>
                            <Button classes="bg-red-500 hover:bg-red-300" click={doDelete}>Confirmar exclusão</Button>
                            <Button classes="bg-primary" click={() => setConfirm(false)}>Cancelar</Button>
                        </>
                        :
                        <Button classes="bg-red-500 hover:bg-red-300" click={() => setConfirm(true)}>Excluir</Button>
                }
            </div>
            {finalizing ? (
                <FinalizeItemModal
                    user={user}
                    order={order}
                    serial={item}
                    onDone={() => { setFinalizing(false); refresh(); }}
                    onCancel={() => setFinalizing(false)}
                />
            ) : null}
            {changingArt ? (
                <ArtLabelStep
                    user={user}
                    order={order}
                    model={model}
                    serial={item}
                    onDone={() => { setChangingArt(false); refresh(); }}
                    onCancel={() => setChangingArt(false)}
                />
            ) : null}
            <LoadingOverlay loading={loading} />
        </div>
    );
};

export default ExtendedArtItemRow;
